import React, { useCallback, useEffect, useState } from "react";
import Nav from "react-bootstrap/Nav";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { fetchClubShareLink } from "../../api/clubApi";
import {
  downloadQr,
  unwrapClubShareLink,
} from "../../helpers/clubShareQr";
import { showToast } from "../../store/features/toast/toastSlice";
import "./clubOfflineQr.css";

/** @typedef {"website" | "book-latest"} ShareTarget */

/**
 * Offline print QR — club website editor / manager dashboard.
 * @param {{ clubId: string, clubName?: string, clubSlug?: string }} props
 */
const ClubOfflineQrPanel = ({ clubId, clubName = "", clubSlug = "" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  /** @type {[ShareTarget, React.Dispatch<React.SetStateAction<ShareTarget>>]} */
  const [target, setTarget] = useState("book-latest");
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const id = String(clubId || "").trim();
    if (!id) {
      setLoading(false);
      setData(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    setError("");
    fetchClubShareLink(id, { target })
      .then((raw) => {
        if (cancel) return;
        const link = unwrapClubShareLink(raw);
        if (!link?.qrDataUrl) {
          throw new Error(t("CLUB_OFFLINE_QR_LOAD_ERROR"));
        }
        setData(link);
      })
      .catch((err) => {
        if (cancel) return;
        setData(null);
        setError(
          typeof err === "string"
            ? err
            : err?.message || t("CLUB_OFFLINE_QR_LOAD_ERROR")
        );
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [clubId, target, t]);

  const shareUrl = String(data?.shareUrl || "").trim();
  const qrDataUrl = String(data?.qrDataUrl || "").trim();
  const displayClubName =
    String(data?.clubName || clubName || "").trim() || t("CLUB_OFFLINE_QR_CLUB_FALLBACK");

  const onCopyLink = useCallback(async () => {
    if (!shareUrl || copying) return;
    setCopying(true);
    try {
      await navigator.clipboard.writeText(shareUrl);
      dispatch(
        showToast({
          message: t("CLUB_OFFLINE_QR_LINK_COPIED"),
          type: "success",
        })
      );
    } catch {
      dispatch(
        showToast({
          message: t("CLUB_OFFLINE_QR_COPY_ERROR"),
          type: "error",
        })
      );
    } finally {
      setCopying(false);
    }
  }, [copying, dispatch, shareUrl, t]);

  const onDownload = useCallback(() => {
    if (!qrDataUrl) return;
    const slugPart = String(clubSlug || data?.slug || "club").trim() || "club";
    const suffix = target === "book-latest" ? "book-qr" : "website-qr";
    downloadQr(qrDataUrl, `${slugPart}-${suffix}.png`);
  }, [clubSlug, data?.slug, qrDataUrl, target]);

  const onOpenLink = useCallback(() => {
    if (!shareUrl) return;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  }, [shareUrl]);

  if (!clubId) return null;

  return (
    <details
      className="club-offline-qr club-website-section border rounded p-3 club-website-advanced"
      open
    >
      <summary className="club-offline-qr__summary fw-semibold">
        <span>{t("CLUB_OFFLINE_QR_TITLE")}</span>
        <i className="bx bx-chevron-down club-offline-qr__chevron" aria-hidden />
      </summary>

      <div className="club-offline-qr__content pt-3">
        <p className="text-muted small mb-3">{t("CLUB_OFFLINE_QR_INTRO")}</p>

        <Nav variant="pills" className="club-offline-qr__tabs mb-3">
          <Nav.Item>
            <Nav.Link
              active={target === "website"}
              onClick={() => setTarget("website")}
              eventKey="website"
            >
              {t("CLUB_OFFLINE_QR_TAB_WEBSITE")}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={target === "book-latest"}
              onClick={() => setTarget("book-latest")}
              eventKey="book-latest"
            >
              {t("CLUB_OFFLINE_QR_TAB_BOOK_LATEST")}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {target === "book-latest" ? (
          <p className="club-offline-qr__recommended small text-muted mb-3">
            {t("CLUB_OFFLINE_QR_BOOK_LATEST_HINT")}
          </p>
        ) : null}

        {loading ? (
          <p className="text-muted mb-0">{t("CLUB_OFFLINE_QR_LOADING")}</p>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : (
          <div className="club-offline-qr__body">
            <div className="club-offline-qr__preview-wrap">
              <img
                src={qrDataUrl}
                alt={t("CLUB_OFFLINE_QR_PREVIEW_ALT", { clubName: displayClubName })}
                className="club-offline-qr__preview"
                width={240}
                height={240}
              />
            </div>

            <div className="club-offline-qr__actions d-flex flex-wrap gap-2 mb-0">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onDownload}
                disabled={!qrDataUrl}
              >
                <i className="bx bx-download me-1" aria-hidden />
                {t("CLUB_OFFLINE_QR_DOWNLOAD")}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onCopyLink}
                disabled={!shareUrl || copying}
              >
                <i className="bx bx-link-alt me-1" aria-hidden />
                {copying ? t("TABLE_SHEET_COPYING") : t("CLUB_OFFLINE_QR_COPY_LINK")}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onOpenLink}
                disabled={!shareUrl}
              >
                <i className="bx bx-link-external me-1" aria-hidden />
                {t("CLUB_OFFLINE_QR_OPEN_LINK")}
              </button>
            </div>
          </div>
        )}
      </div>
    </details>
  );
};

export default ClubOfflineQrPanel;
